import { useEffect, useId, useRef, useState } from 'react';
import { fetchCommanderCandidates, type CommanderCandidate } from '../api/missions';
import { ca } from '../i18n/ca';

/**
 * The field that names an account for the commander of the night: as soon as something is
 * typed it lists the accounts whose username or shown name contains it, and picking one fills
 * in the username (the owner, web card 223, 2026-10-19: it was a free text box, and a shown
 * name typed where the username was wanted named nobody). Typing on without picking still
 * sends what was typed; the platform finds the account by username or by its shown name.
 */
export function AccountPicker({
  code = '',
  search,
  value,
  onChange,
  onPick,
  label,
  exclude = [],
  autoFocus = false,
}: {
  code?: string;
  /** Asks something other than the commander's candidates (the editors to invite). */
  search?: (typed: string) => Promise<CommanderCandidate[]>;
  value: string;
  onChange: (text: string) => void;
  /** Called with the account chosen from the list; without it, picking fills the field. */
  onPick?: (candidate: CommanderCandidate) => void;
  label: string;
  /** Handles not to offer (already a candidate, or the commander handing over). */
  exclude?: string[];
  autoFocus?: boolean;
}) {
  const [found, setFound] = useState<CommanderCandidate[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  /** The text the list answers, so "nobody" is said only once the platform has answered. */
  const [answered, setAnswered] = useState('');
  const listId = useId();
  const typed = value.trim();
  const skip = exclude.map((h) => h.toLowerCase()).join('\n');
  const asked = useRef(0);
  const searchRef = useRef(search);
  searchRef.current = search;

  useEffect(() => {
    if (!typed) {
      setFound([]);
      return;
    }
    const ticket = ++asked.current;
    const timer = setTimeout(() => {
      (searchRef.current ? searchRef.current(typed) : fetchCommanderCandidates(code, typed))
        .then((list) => {
          if (ticket !== asked.current) return;
          const without = skip ? skip.split('\n') : [];
          setFound(list.filter((c) => !without.includes(c.handle.toLowerCase())));
          setAnswered(typed);
          setActive(-1);
        })
        .catch(() => {
          if (ticket !== asked.current) return;
          setFound([]);
          setAnswered('');
        });
    }, 150);
    return () => clearTimeout(timer);
  }, [code, typed, skip]);

  const shown = open && typed !== '' ? found : [];
  const pick = (c: CommanderCandidate) => {
    if (onPick) onPick(c);
    else onChange(c.handle);
    setOpen(false);
    setActive(-1);
  };

  return (
    <div className="account-picker">
      <input
        role="combobox"
        className="control"
        aria-label={label}
        placeholder={label}
        aria-autocomplete="list"
        aria-expanded={shown.length > 0}
        aria-controls={listId}
        aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
        autoComplete="off"
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && shown.length) {
            e.preventDefault();
            setActive((i) => (i + 1) % shown.length);
          } else if (e.key === 'ArrowUp' && shown.length) {
            e.preventDefault();
            setActive((i) => (i <= 0 ? shown.length - 1 : i - 1));
          } else if (e.key === 'Enter' && active >= 0 && shown[active]) {
            e.preventDefault();
            pick(shown[active]);
          } else if (e.key === 'Enter' && onPick) {
            // A field that adds to a list adds on Enter, never submits the form around it.
            e.preventDefault();
            if (typed) pick(shown[0] ?? { handle: typed, displayName: typed });
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {shown.length > 0 && (
        <ul className="account-picker-list" id={listId} role="listbox" aria-label={label}>
          {shown.map((c, i) => (
            <li
              key={c.handle}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              className={i === active ? 'active' : undefined}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(c);
              }}
            >
              <i className="bi bi-person" aria-hidden="true" />
              <span className="grow">{c.displayName}</span>
              <span className="muted small">@{c.handle}</span>
            </li>
          ))}
        </ul>
      )}
      {open && typed !== '' && answered === typed && found.length === 0 && (
        <p className="muted small account-picker-none" role="status">
          {ca.live.pickerNone}
        </p>
      )}
    </div>
  );
}
